interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function buildPages(page: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: (number | string)[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  if (start > 2) pages.push('start-ellipsis');
  for (let index = start; index <= end; index += 1) pages.push(index);
  if (end < totalPages - 1) pages.push('end-ellipsis');
  pages.push(totalPages);

  return pages;
}

export default function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = buildPages(page, totalPages);

  return (
    <nav className="flex items-center justify-center gap-1.5 flex-wrap" aria-label="分页导航">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="h-9 px-3 flex items-center gap-1 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
        <span className="hidden sm:inline">上一页</span>
      </button>

      {pages.map((item) =>
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            aria-current={item === page ? 'page' : undefined}
            className={`w-9 h-9 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              item === page
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
            }`}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="w-6 text-center text-sm text-foreground-400 select-none">
            …
          </span>
        ),
      )}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="h-9 px-3 flex items-center gap-1 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
      >
        <span className="hidden sm:inline">下一页</span>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
    </nav>
  );
}